<template>
	<v-container>
		<v-card class="ant-card py-2 px-4" elevation="0">
			<v-toolbar flat dense>
				<div class="v-typography--title1">{{ $t("products") }}</div>
			</v-toolbar>
			<v-divider class="mb-4" />
			<v-card-text class="pb-0 px-0">
				<common-table
					:headers="productListHeaders"
					:items="productsWithNames"
					:hide-pagination="true"
					:entity-name="$t('product')"
					@row-click="rowClickHandler"
				>
					<template #item.name="{ item }">
						{{ item.formattedProduct }}
					</template>
					<template #item.dimensions="{ item }">
						{{ item.dimensions }}
					</template>
					<template #item.quantity="{ item }"> {{ item.quantity }} {{ item.unitCode }} </template>
					<template #item.amount="{ item }">
						{{
							item.total.toLocaleString($lang, {
								style: "currency",
								currency: currencyCode,
							})
						}}
					</template>
					<template #body.append v-if="productsWithNames.length">
						<tr>
							<td class="text-right" colspan="4">
								{{ $t("total") }}:
								{{
									totalAmount.toLocaleString($lang, {
										style: "currency",
										currency: currencyCode,
									})
								}}
								{{ currencySymbol }}
							</td>
						</tr>
					</template>
				</common-table>
				<v-row v-if="totalAmount" align="center" class="px-4 mt-2 pb-1" justify="space-between">
					<v-col cols="auto" class="d-flex align-center">
						<v-icon color="black" class="mr-2">icon-money</v-icon>
						<span class="v-typography--body1 black--text">{{ $t("total_price") }}</span>
					</v-col>
					<v-col cols="auto" class="text-right">
						<span class="text-lg font-weight-bold black--text">
							{{
								totalAmount.toLocaleString($lang, {
									style: "currency",
									currency: currencyCode,
								})
							}}
						</span>
					</v-col>
				</v-row>
				<v-divider
					class="mt-2"
					v-if="
						[proformaStatus.Template, proformaStatus.PendingRevise, proformaStatus.Revoked].includes(
							currentItem.status
						) && iAmSender
					"
				/>
				<v-row
					class="mt-1 px-2"
					align="center"
					v-if="
						[proformaStatus.Template, proformaStatus.PendingRevise, proformaStatus.Revoked].includes(
							currentItem.status
						) && iAmSender
					"
				>
					<v-col>
						<v-btn color="accent" outlined large elevation="0" block @click="productDialog.show()">
							<v-icon left>icon-plus</v-icon>
							{{ $t("add_item", { item: $t("product") }) }}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
		<proforma-invoice-product-dialog
			v-model="productDialog.visibility"
			:item="productDialog.item"
			:proformaInvoice="currentItem"
			@saved="productDialog.saved"
			@deleted="productDialog.deleted"
		/>
	</v-container>
</template>

<script>
import ProformaInvoiceProductDialog from "../dialogs/ProformaInvoiceProductDialog.vue";
import CommonTable from "../../../partials/CommonTable.vue";
import { proformaStatus } from "../../../../utils/enum";
import { loadProformaProducts } from "@/utils/proformaHelper.js";

export default {
	name: "ProformaInvoiceProducts",
	components: { ProformaInvoiceProductDialog, CommonTable },
	props: {
		currentItem: { type: Object, required: true },
		iAmSender: { type: Boolean, default: false },
	},
	data() {
		return {
			productListHeaders: [
				{ text: this.$t("name"), value: "name", sortable: false },
				{ text: this.$t("dimensions"), value: "dimensions", sortable: false },
				{ text: this.$t("quantity"), value: "quantity", align: "end", sortable: false },
				{ text: this.$t("price"), value: "amount", align: "end", sortable: false },
			],
			productDialog: {
				visibility: false,
				item: null,
				show: (item) => {
					this.productDialog.item = item;
					this.productDialog.visibility = true;
				},
				hide: () => {
					this.productDialog.item = null;
					this.productDialog.visibility = false;
				},
				saved: () => {
					this.$emit("reloadCurrentItem");
					this.productDialog.hide();
				},
				deleted: () => {
					this.$emit("reloadCurrentItem");
					this.productDialog.hide();
				},
			},
			productsWithNames: [],
			totalAmount: 0,
		};
	},
	computed: {
		$lang() {
			return this.$i18n.locale;
		},
		currencyCode() {
			return this.currentItem.currency?.code || "USD";
		},
		currencySymbol() {
			return this.currentItem.currency?.symbol || "$";
		},
		proformaStatus() {
			return proformaStatus;
		},
	},
	watch: {
		currentItem: {
			immediate: true,
			handler() {
				this.refresh();
			},
		},
	},
	methods: {
		async refresh() {
			const { items, totalAmount } = await loadProformaProducts(this.currentItem, this);
			this.productsWithNames = items;
			this.totalAmount = totalAmount;
		},
		rowClickHandler(item) {
			if (
				this.iAmSender &&
				![
					proformaStatus.PendingApproval,
					proformaStatus.Rejected,
					proformaStatus.Approved,
					proformaStatus.Processed,
				].includes(this.currentItem.status)
			) {
				this.productDialog.show(item);
			}
		},
	},
};
</script>
